import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Input } from '@heyroot/ui/primitives/Input';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import { useFeedbackSubmissions, useFeedbackSummary } from '../lib/use-feedback';
import {
  ALL_STATUSES,
  formatAge,
  isStale,
  priorityVariant,
  requestTypeVariant,
  statusBadgeVariant,
  statusLabel,
} from '../lib/feedback-fsm';

const PAGE_SIZE = 25;

// "All ..." is a real, re-selectable filter state — Radix SelectItem can
// never carry value="" (check-radix-empty-select), so it needs a sentinel
// mapped back to '' at each filter boundary.
const ALL = '__all__';

const REQUEST_TYPES = ['bug', 'feature', 'enhancement'] as const;
const PRIORITIES = ['critical', 'high', 'medium', 'low'] as const;

type AssigneeFilter = '' | 'me' | 'unassigned';

export function FeedbackInboxPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [requestType, setRequestType] = useState('');
  const [priority, setPriority] = useState('');
  const [assignee, setAssignee] = useState<AssigneeFilter>('');
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [rawModule, setRawModule] = useState('');
  const [module, setModule] = useState('');
  const [rawQ, setRawQ] = useState('');
  const [q, setQ] = useState('');
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursorStack[cursorStack.length - 1];

  // Debounce the free-text filters 300ms — mirrors tenants-list.tsx's search debounce so we
  // don't fire a query + reset pagination on every keystroke.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Only act when a free-text filter actually changed. This effect also runs on mount; an
      // unconditional reset here would undo a Next click made in the first 300ms.
      if (rawModule === module && rawQ.trim() === q) return;
      setModule(rawModule);
      setQ(rawQ.trim());
      setCursorStack([undefined]);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [rawModule, rawQ]);

  // Filters shared by the table and the per-status chips (the chips ignore `status` itself).
  const sharedFilters = {
    ...(module ? { module } : {}),
    ...(requestType ? { requestType } : {}),
    ...(priority ? { priority } : {}),
    ...(assignee ? { assignee } : {}),
    ...(q ? { q } : {}),
  };

  const { data, isPending, isError, refetch } = useFeedbackSubmissions({
    ...sharedFilters,
    ...(status ? { status } : {}),
    ...(sort !== 'newest' ? { sort } : {}),
    ...(cursor ? { cursor } : {}),
    limit: PAGE_SIZE,
  });
  const summary = useFeedbackSummary(sharedFilters);

  const rows = data?.rows ?? [];
  const nextCursor = data?.nextCursor ?? null;
  const page = cursorStack.length;
  const counts = summary.data?.counts ?? {};
  const total = summary.data?.total ?? null;
  const filtered = Boolean(status || module || requestType || priority || assignee || q);

  const resetPaging = () => setCursorStack([undefined]);
  const clearFilters = () => {
    setStatus('');
    setRequestType('');
    setPriority('');
    setAssignee('');
    setSort('newest');
    setRawModule('');
    setModule('');
    setRawQ('');
    setQ('');
    resetPaging();
  };

  if (isError) return <ErrorState onRetry={() => void refetch()} />;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-heading-2 text-primary">Feedback Triage</h1>
        <p className="mt-1 text-body-sm text-muted">
          Feature requests, enhancements, and bug reports submitted from the field.
        </p>
      </div>

      {/* Per-status counts: what is waiting where, at a glance; click to filter. */}
      <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Filter by status">
        <Button
          size="sm"
          variant={status === '' ? 'emphasis' : 'secondary'}
          aria-pressed={status === ''}
          onClick={() => {
            setStatus('');
            resetPaging();
          }}
        >
          All{total != null ? ` (${total})` : ''}
        </Button>
        {ALL_STATUSES.map((s) => (
          <Button
            key={s}
            size="sm"
            variant={status === s ? 'emphasis' : 'secondary'}
            aria-pressed={status === s}
            onClick={() => {
              setStatus(s);
              resetPaging();
            }}
          >
            {statusLabel(s)} ({counts[s] ?? 0})
          </Button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <Select
          value={requestType || ALL}
          onValueChange={(v) => {
            setRequestType(v === ALL ? '' : v);
            resetPaging();
          }}
        >
          <SelectTrigger aria-label="Filter by type" className="h-[1.875rem] w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {REQUEST_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={priority || ALL}
          onValueChange={(v) => {
            setPriority(v === ALL ? '' : v);
            resetPaging();
          }}
        >
          <SelectTrigger aria-label="Filter by priority" className="h-[1.875rem] w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All priorities</SelectItem>
            {PRIORITIES.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={assignee || ALL}
          onValueChange={(v) => {
            setAssignee(v === 'me' || v === 'unassigned' ? v : '');
            resetPaging();
          }}
        >
          <SelectTrigger aria-label="Filter by assignee" className="h-[1.875rem] w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Anyone</SelectItem>
            <SelectItem value="me">Assigned to me</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={sort}
          onValueChange={(v) => {
            setSort(v === 'oldest' ? 'oldest' : 'newest');
            resetPaging();
          }}
        >
          <SelectTrigger aria-label="Sort order" className="h-[1.875rem] w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1 basis-48 max-w-64">
          <Input
            type="text"
            placeholder="Search title or description…"
            value={rawQ}
            maxLength={100}
            onChange={(e) => setRawQ(e.target.value)}
            aria-label="Search feedback"
          />
        </div>
        <div className="flex-1 basis-40 max-w-56">
          <Input
            type="text"
            placeholder="Filter by module…"
            value={rawModule}
            // The server rejects a module over 100 chars with a 400, which replaces the whole page
            // (inputs included) with an error state -- cap it here so that can't happen.
            maxLength={100}
            onChange={(e) => setRawModule(e.target.value)}
            aria-label="Filter by module"
          />
        </div>
        {filtered && (
          <Button size="sm" variant="link" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {!isPending && rows.length === 0 && (
        <EmptyState
          title="No feedback matches these filters"
          description={
            filtered
              ? 'No results for these filters.'
              : 'Submitted feedback appears here for triage.'
          }
        />
      )}

      {(isPending || rows.length > 0) && (
        <>
          <div className="overflow-x-auto">
            <Table dense>
              <TableHead>
                <tr>
                  <TableHeaderCell>Title</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Priority</TableHeaderCell>
                  <TableHeaderCell>Tenant</TableHeaderCell>
                  <TableHeaderCell>Module</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Age</TableHeaderCell>
                  <TableHeaderCell>Assignee</TableHeaderCell>
                  <TableHeaderCell>Submitted by</TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {isPending ? (
                  <SkeletonTable rows={8} cols={9} />
                ) : (
                  rows.map((row) => {
                    const stale = isStale(row.status, row.createdAt);
                    return (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer"
                        onClick={() =>
                          void navigate({ to: '/feedback/$id', params: { id: row.id } })
                        }
                      >
                        <TableCell>
                          <span className="line-clamp-2 text-body-sm text-primary">
                            {row.title}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={requestTypeVariant(row.requestType)}>
                            {row.requestType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={priorityVariant(row.priority)}>{row.priority}</Badge>
                        </TableCell>
                        <TableCell className="text-body-sm text-secondary">
                          {row.tenantName ?? (
                            <span className="text-muted" title={row.tenantId}>
                              {row.tenantId.slice(0, 8)}…
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-body-sm text-secondary">
                          {row.module}
                          {row.submodule ? ` / ${row.submodule}` : ''}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusBadgeVariant(row.status)}>
                            {statusLabel(row.status)}
                          </Badge>
                        </TableCell>
                        <TableCell
                          className={
                            stale ? 'text-caption text-warning-text' : 'text-caption text-muted'
                          }
                          title={`${new Date(row.createdAt).toLocaleString()}${stale ? ' — waiting on triage for a week or more' : ''}`}
                        >
                          {formatAge(row.createdAt)}
                          {stale ? ' (stale)' : ''}
                        </TableCell>
                        <TableCell className="text-body-sm text-secondary">
                          {row.assigneeName ?? <span className="text-muted">Unassigned</span>}
                        </TableCell>
                        <TableCell className="text-body-sm text-secondary">
                          {row.submittedByName ?? <span className="text-muted">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {!isPending && (page > 1 || nextCursor) && (
            <div className="flex items-center justify-end gap-2 pt-2">
              <span className="text-body-sm text-muted">Page {page}</span>
              <Button
                size="sm"
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setCursorStack((s) => (s.length > 1 ? s.slice(0, -1) : s))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!nextCursor}
                onClick={() => {
                  if (nextCursor) setCursorStack((s) => [...s, nextCursor]);
                }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
